import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { motion, Variants, useInView } from "framer-motion";
import { keyframes } from "@emotion/react";
import celsionBankLogo from "../../assets/celsion-bank.png";
import talosLogo from "../../assets/talos.png";
import wydenLogo from "../../assets/wyden.png";
import metacoLogo from "../../assets/metaco.png";
import b2c2Logo from "../../assets/b2c2.png";
import ellipticLogo from "../../assets/eliptic.png";
import mambuLogo from "../../assets/mambu.png";
import thoughtMachineLogo from "../../assets/thought_machine.png";
import finnovaLogo from "../../assets/finnova.png";

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.2, ease: [0.16, 0.77, 0.47, 0.97] },
  },
};

const viewport = { once: true, amount: 0.1 };

const LOGO_ROWS = [
  [
    { src: celsionBankLogo, alt: "Celsion Bank" },
    { src: finnovaLogo, alt: "Finnova" },
    { src: mambuLogo, alt: "Mambu" },
  ],
  [
    { src: thoughtMachineLogo, alt: "Thought Machine" },
    { src: wydenLogo, alt: "Wyden" },
    { src: talosLogo, alt: "TALOS" },
  ],
  [
    { src: b2c2Logo, alt: "B2C2" },
    { src: metacoLogo, alt: "Metaco" },
    { src: ellipticLogo, alt: "Elliptic" },
  ],
];

const colorWave = keyframes`
  0%, 12%, 88%, 100% {
    filter: grayscale(100%);
    opacity: 0.5;
  }
  42%, 58% {
    filter: grayscale(0%);
    opacity: 1;
  }
`;

const CYCLE = 7; // seconds per full wave cycle
const COL_OFFSET = 0.55; // seconds between adjacent columns
const ROW_OFFSET = 0.2; // seconds between adjacent rows

const TrustedBy: React.FC = () => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const isInView = useInView(gridRef, { once: false, amount: 0.3 });

  return (
    <Box
      component="section"
      data-nosnippet
      sx={{
        bgcolor: "#f8f9fb",
        borderBottom: "1px solid rgba(29, 47, 65, 0.15)",
      }}
    >
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 3, md: 6 },
          py: { xs: 8, md: 12 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 8, md: 12 },
        }}
      >
        <Box sx={{ flexShrink: 0, width: { md: "300px" } }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={BLOCK}
          >
            <motion.div variants={ITEM} style={{ willChange: "transform" }}>
              <Typography
                sx={{
                  fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
                  fontSize: { xs: "1.75rem", md: "2.2rem" },
                  fontWeight: 600,
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                  color: "#1D2F41",
                  mb: 3,
                }}
              >
                {t("trustedBy.heading")}
              </Typography>
            </motion.div>
            <motion.div variants={ITEM} style={{ willChange: "transform" }}>
              <Typography
                sx={{
                  fontFamily: '"Calibri", sans-serif',
                  fontSize: "1rem",
                  fontWeight: 300,
                  lineHeight: 1.7,
                  color: "rgba(29, 47, 65, 0.65)",
                }}
              >
                {t("trustedBy.body")}
              </Typography>
            </motion.div>
          </motion.div>
        </Box>

        <Box ref={gridRef} sx={{ flex: 1 }}>
          {LOGO_ROWS.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {rowIndex > 0 && (
                <Box sx={{ position: "relative", height: "1px" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "calc(33.33% - 6px)",
                      height: "1px",
                      bgcolor: "rgba(29, 47, 65, 0.15)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: "calc(33.33% + 6px)",
                      width: "calc(33.34% - 12px)",
                      height: "1px",
                      bgcolor: "rgba(29, 47, 65, 0.15)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: "calc(66.67% + 6px)",
                      right: 0,
                      height: "1px",
                      bgcolor: "rgba(29, 47, 65, 0.15)",
                    }}
                  />
                </Box>
              )}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                variants={BLOCK}
              >
                <Box sx={{ display: "flex", alignItems: "stretch" }}>
                  {row.map(({ src, alt }, colIndex) => {
                    const delay = colIndex * COL_OFFSET + rowIndex * ROW_OFFSET;
                    return (
                      <React.Fragment key={alt}>
                        {colIndex > 0 && (
                          <Box
                            sx={{
                              width: "1px",
                              bgcolor: "rgba(29, 47, 65, 0.15)",
                              alignSelf: "stretch",
                              flexShrink: 0,
                              my: "6px",
                            }}
                          />
                        )}
                        <motion.div
                          variants={ITEM}
                          style={{ willChange: "transform", flex: 1 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              py: 5,
                            }}
                          >
                            <Box
                              component="img"
                              src={src}
                              alt={alt}
                              loading="lazy"
                              sx={{
                                width: { xs: "80px", md: "110px" },
                                height: { xs: "36px", md: "44px" },
                                objectFit: "contain",
                                animation: `${colorWave} ${CYCLE}s ease-in-out infinite`,
                                animationDelay: `${delay}s`,
                                animationPlayState: isInView
                                  ? "running"
                                  : "paused",
                                cursor: "default",
                              }}
                            />
                          </Box>
                        </motion.div>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </motion.div>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TrustedBy;
